import React, { useEffect, useRef, useState, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import Sidebar from './components/Sidebar';
import { connectAdminSocket, disconnectSocket } from './socket';
import { sendBrowserNotification, playAlertBeep } from './utils/browserNotification';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import TablesPage from './pages/TablesPage';
import OrdersPage from './pages/OrdersPage';
import MenuManagePage from './pages/MenuManagePage';
import SettingsPage from './pages/SettingsPage';
import ReportsPage from './pages/ReportsPage';
import StaffCallPage from './pages/StaffCallPage';
import CouponPage from './pages/CouponPage';
// New production pages
import KycPage from './pages/KycPage';
import SettlementPage from './pages/SettlementPage';
import OnboardingPage from './pages/OnboardingPage';
import HelpCenterPage from './pages/HelpCenterPage';
import TermsAdminPage from './pages/TermsAdminPage';
import RefundPage from './pages/RefundPage';
import ReceiptPage from './pages/ReceiptPage';
import AllergenPage from './pages/AllergenPage';
import MonitoringPage from './pages/MonitoringPage';
import NotificationSettingsPage from './pages/NotificationSettingsPage';
import BillingPage from './pages/BillingPage';
import PlanSelectionPage from './pages/PlanSelectionPage';
import InvoiceDetailPage from './pages/InvoiceDetailPage';
import PlatformAdminPage from './pages/PlatformAdminPage';
import MasterLoginPage from './pages/MasterLoginPage';
import MasterDashboardPage from './pages/MasterDashboardPage';
import MasterStoresPage from './pages/MasterStoresPage';
import MasterSubscriptionsPage from './pages/MasterSubscriptionsPage';

// Loading screen
const LoadingScreen = () => (
  <div className="loading-screen">
    <div className="spinner spinner-dark" style={{ width: 36, height: 36, borderWidth: 3 }} />
    <span>불러오는 중...</span>
  </div>
);

// Protected route: redirect to /login if not authenticated
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
};

// Public route: redirect to /dashboard if already logged in
const PublicRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <LoadingScreen />;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return children;
};

// Layout wrapper with Sidebar for protected pages
const AdminLayout = ({ children }) => {
  return (
    <div className="app-layout">
      <Sidebar />
      <main className="main-content">
        {children}
      </main>
    </div>
  );
};

// Global toast component (minimal, sits above everything)
let globalToastIdCounter = 0;

const GlobalToastContainer = ({ toasts }) => {
  if (!toasts.length) return null;
  return (
    <div
      style={{
        position: 'fixed',
        top: 16,
        right: 16,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        pointerEvents: 'none',
      }}
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          style={{
            padding: '12px 18px',
            borderRadius: 10,
            fontSize: 14,
            fontWeight: 600,
            color: '#fff',
            background: t.type === 'warning' ? '#F04452' : '#191F28',
            boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
            maxWidth: 320,
            lineHeight: 1.4,
          }}
        >
          {t.msg}
        </div>
      ))}
    </div>
  );
};

// Global socket listener - active for all authenticated pages
const GlobalNotificationListener = () => {
  const { user, isAuthenticated } = useAuth();
  const socketRef = useRef(null);
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((msg, type = 'info') => {
    const id = ++globalToastIdCounter;
    setToasts((t) => [...t, { id, msg, type }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 5000);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      disconnectSocket();
      socketRef.current = null;
      return;
    }

    const storeId = user?.storeId || user?.store_id;
    if (!storeId) return;

    const socket = connectAdminSocket(storeId);
    socketRef.current = socket;

    const handleNewOrder = (data) => {
      const tableNum = data.tableNumber || data.table_number;
      playAlertBeep();
      addToast(`새 주문 접수 - 테이블 ${tableNum}번`, 'info');
      sendBrowserNotification('새 주문 접수', `테이블 ${tableNum}번 주문이 들어왔습니다`);
    };

    const handleStaffCall = (data) => {
      const tableNum = data.tableNumber || data.table_number;
      playAlertBeep();
      addToast(`직원 호출 - 테이블 ${tableNum}번`, 'warning');
      sendBrowserNotification('직원 호출', `테이블 ${tableNum}번에서 호출합니다`);
    };

    socket.on('new_order', handleNewOrder);
    socket.on('staff_call', handleStaffCall);

    return () => {
      socket.off('new_order', handleNewOrder);
      socket.off('staff_call', handleStaffCall);
    };
  }, [isAuthenticated, user, addToast]);

  return <GlobalToastContainer toasts={toasts} />;
};

const AppRoutes = () => {
  return (
    <>
    <GlobalNotificationListener />
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Protected routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <DashboardPage />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tables"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <TablesPage />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <OrdersPage />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/menu"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <MenuManagePage />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <SettingsPage />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <ReportsPage />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/staff-calls"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <StaffCallPage />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/coupons"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <CouponPage />
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      {/* New production routes */}
      <Route path="/kyc" element={<ProtectedRoute><AdminLayout><KycPage /></AdminLayout></ProtectedRoute>} />
      <Route path="/settlement" element={<ProtectedRoute><AdminLayout><SettlementPage /></AdminLayout></ProtectedRoute>} />
      <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
      <Route path="/help" element={<ProtectedRoute><AdminLayout><HelpCenterPage /></AdminLayout></ProtectedRoute>} />
      <Route path="/terms-admin" element={<ProtectedRoute><AdminLayout><TermsAdminPage /></AdminLayout></ProtectedRoute>} />
      <Route path="/refunds" element={<ProtectedRoute><AdminLayout><RefundPage /></AdminLayout></ProtectedRoute>} />
      <Route path="/receipts" element={<ProtectedRoute><AdminLayout><ReceiptPage /></AdminLayout></ProtectedRoute>} />
      <Route path="/allergens" element={<ProtectedRoute><AdminLayout><AllergenPage /></AdminLayout></ProtectedRoute>} />
      <Route path="/monitoring" element={<ProtectedRoute><AdminLayout><MonitoringPage /></AdminLayout></ProtectedRoute>} />
      <Route path="/notification-settings" element={<ProtectedRoute><AdminLayout><NotificationSettingsPage /></AdminLayout></ProtectedRoute>} />
      <Route path="/billing" element={<ProtectedRoute><AdminLayout><BillingPage /></AdminLayout></ProtectedRoute>} />
      <Route path="/plans" element={<ProtectedRoute><AdminLayout><PlanSelectionPage /></AdminLayout></ProtectedRoute>} />
      <Route path="/invoice/:id" element={<ProtectedRoute><AdminLayout><InvoiceDetailPage /></AdminLayout></ProtectedRoute>} />

      {/* Master admin (separate auth) */}
      <Route path="/platform-admin" element={<PlatformAdminPage />} />
      <Route path="/master/login" element={<MasterLoginPage />} />
      <Route path="/master" element={<MasterDashboardPage />} />
      <Route path="/master/stores" element={<MasterStoresPage />} />
      <Route path="/master/subscriptions" element={<MasterSubscriptionsPage />} />

      {/* Default redirects */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
    </>
  );
};

const App = () => {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;
